import { get, writable } from "svelte/store";
import moment from "moment";

import lineArc from "@turf/line-arc";
import { point, polygon } from "@turf/helpers";

import LoadState from "../../Enums/LoadState";
import GeoHelper from "../../Helpers/GeoHelper";
import DistanceUtil from "../MeasureTool/DistanceUtil";
import DistanceUnit from "../MeasureTool/DistanceUnit";

import MapStore from "../../Map/Store";
import MapDataSource from "../../Map/Enums/MapDataSource";
import VesselGroup from "../../Models/VesselGroup";

import Filters from "../../Filters/Filters";
import AlertFilterProperties from "../../Alerts/AlertFilterProperties";
import VesselsNearbyPrefilterProperties from "./VesselsNearbyPrefilterProperties";
import MapLayer from "../../Map/Enums/MapLayer";
import { Layout, Paint } from "../../Map/MapLayerStyles";
import MapLayerManager from "../../Map/MapLayerManager";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";

const VesselsNearbyStore = {
	meta: {
		loadState: writable(LoadState.INIT),
		progress: writable(0),
		error: writable(""),
		lastUpdated: writable(0),
		mapInitted: writable(false)
	},
	query: {
		raw: {
			useVessel: writable(null),
			baseDatetime: writable(moment(Date.now())),
			timeWindowHrs: writable(12),
			lngLat: writable({ lng: 0, lat: 0 }),
			radiusNm: writable(10)
		},
		computed: {
			fromDatetime: writable(new Date()),
			toDatetime: writable(new Date()),
			lineString: writable(GeoHelper.emptyGeoJson()),
			polygon: writable(GeoHelper.emptyGeoJson())
		}
	},
	preFilters: {
		advanced: new Filters(VesselsNearbyPrefilterProperties),
		lastUpdated: writable(new Date(0))
	},
	filters: {
		advanced: new Filters(AlertFilterProperties),
		lastUpdated: writable(new Date(0))
	},
	results: {
		meta: {
			total: writable(0), // The actual number of vessels in the results. We only show `limit` results.
			limit: writable(100)
		},
		vesselGroup: new VesselGroup(),
		tableData: {
			filtered: writable([])
		},
		getFilteredVessels: () => {
			const vessels = get(VesselsNearbyStore.results.vesselGroup.vessels);
			const filtered = vessels.filter((vessel) => {
				if (VesselsNearbyStore.filters.advanced.length > 0) {
					return VesselsNearbyStore.filters.advanced.apply([vessel]).length === 1;
				}
				return true;
			});
			return filtered;
		}
	},
	actions: {
		initMap: (map) => {
			if (get(VesselsNearbyStore.meta.mapInitted)) {
				return;
			}
			map.addSource(MapDataSource.VESSELS_NEARBY, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.VESSELS_NEARBY_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});

			map.addLayer(
				{
					id: MapLayer.VESSELS_NEARBY_LINES,
					type: "line",
					source: MapDataSource.VESSELS_NEARBY_LINES,
					layout: Layout.measure,
					paint: Paint.measure.lines
				}
			);
			map.addLayer({
				id: MapLayer.VESSELS_NEARBY,
				type: "symbol",
				source: MapDataSource.VESSELS_NEARBY,
				layout: Layout.vessel
			});

			MapLayerManager.addLayerGroup(MapLayerGroup.VESSELS_NEARBY, [
				MapLayer.VESSELS_NEARBY_LINES,
				MapLayer.VESSELS_NEARBY
			], [
				MapDataSource.VESSELS_NEARBY_LINES,
				MapDataSource.VESSELS_NEARBY
			]);

			VesselsNearbyStore.meta.mapInitted.set(true);
		},
		deinitMap: () => {
			if (!get(VesselsNearbyStore.meta.mapInitted)) {
				return;
			}
			MapLayerManager.removeLayerGroup(MapLayerGroup.VESSELS_NEARBY);
			VesselsNearbyStore.meta.mapInitted.set(false);
		},
		resetMeta: () => {
			VesselsNearbyStore.meta.loadState.set(LoadState.INIT);
			VesselsNearbyStore.meta.progress.set(0);
			VesselsNearbyStore.meta.error.set("");
			VesselsNearbyStore.meta.lastUpdated.set(0);
		},
		resetQuery: () => {
			VesselsNearbyStore.query.raw.useVessel.set(null);
			VesselsNearbyStore.query.raw.baseDatetime.set(moment(Date.now()));
			VesselsNearbyStore.query.raw.timeWindowHrs.set(12);
			VesselsNearbyStore.query.raw.lngLat.set({ lng: 0, lat: 0 });
			VesselsNearbyStore.query.raw.radiusNm.set(10);
		},
		resetResults: () => {
			//  VesselsNearbyStore.filters.selected.spireIds.set([]);
			VesselsNearbyStore.results.vesselGroup.vessels.set([]);
		},
		resetAll: () => {
			VesselsNearbyStore.actions.resetMeta();
			VesselsNearbyStore.actions.resetQuery();
			VesselsNearbyStore.actions.resetResults();

			VesselsNearbyStore.actions.compute();
		},
		compute: () => {
			const d = get(VesselsNearbyStore.query.raw.baseDatetime);
			const tw = get(VesselsNearbyStore.query.raw.timeWindowHrs);
			const fromDate = d.subtract(tw, "hours").toDate();
			const toDate = d.add(tw, "hours").toDate();

			VesselsNearbyStore.query.computed.fromDatetime.set(fromDate);
			VesselsNearbyStore.query.computed.toDatetime.set(toDate);

			const vessel = get(VesselsNearbyStore.query.raw.useVessel);
			const lngLatPoint = (vessel)
				? ((vessel.hasActiveSnapshot())
					? vessel.snapshotCoordinates
					: vessel.coordinates)
				: [get(VesselsNearbyStore.query.raw.lngLat).lng, get(VesselsNearbyStore.query.raw.lngLat).lat];
			const center = point(lngLatPoint);
			const radiusMiles = DistanceUtil.convert(get(VesselsNearbyStore.query.raw.radiusNm), DistanceUnit.NAUTICAL_MILES, DistanceUnit.MILES);
			const arc = lineArc(center, radiusMiles, 0, 360, { steps: 45, units: "miles" });
			arc.properties.color = "black";
			VesselsNearbyStore.query.computed.lineString.set(arc);

			const poly = polygon([arc.geometry.coordinates], { name: "poly1", color: "black" });
			VesselsNearbyStore.query.computed.polygon.set(poly);

			VesselsNearbyStore.helpers.rebuildGeoJSON();
		}
	},
	helpers: {
		rebuildGeoJSON: () => {
			const map = MapStore.helpers.getMap();
			const mapInitialised = get(MapStore.meta.initialised);

			VesselsNearbyStore.actions.initMap(map);

			const mapLayersInitialised = get(VesselsNearbyStore.meta.mapInitted);
			if (map && mapInitialised && mapLayersInitialised) {
				const lineStringFeatureCollection = GeoHelper.emptyGeoJson([get(VesselsNearbyStore.query.computed.lineString)]);
				map.getSource(MapDataSource.VESSELS_NEARBY_LINES).setData(lineStringFeatureCollection);

				const vessels = VesselsNearbyStore.results.getFilteredVessels(); // get(VesselsNearbyStore.results.vesselGroup.vessels);
				const features = [];
				vessels.forEach(vessel => {
					if (vessel.visible) {
						features.push(vessel.currentFeature);
					}
				});
				const fc = GeoHelper.emptyGeoJson(features);
				map.getSource(MapDataSource.VESSELS_NEARBY).setData(fc);
			}
		},

		shouldShowLoadingCard: () => {
			return get(VesselsNearbyStore.meta.loadState) === LoadState.LOADING &&
					get(VesselsNearbyStore.meta.progress) > 0.0;
		},
		shouldShowLoadingSpinner: () => {
			return get(VesselsNearbyStore.meta.loadState) === LoadState.LOADING &&
				get(VesselsNearbyStore.meta.error) === "";
		}
	}

};
export default VesselsNearbyStore;
